import type { ReactNode } from 'react';
import { fmtWeekdayDayMonth } from '../../flight/results/flight_format.js';
import { useCheckoutEnv } from '../drawer/env_context.js';
import {
  conciergeReasonKey,
  type UseConciergeRequestResult,
} from '../form/use_concierge_request.js';
import type { ConciergeCopy } from '../model/checkout_copy.js';
import {
  CONCIERGE_BED_TYPES,
  CONCIERGE_BUS_ROW_POSITIONS,
  CONCIERGE_BUS_SEAT_CLASSES,
  CONCIERGE_BUS_SEAT_POSITIONS,
  CONCIERGE_DETAILS_MAX,
  CONCIERGE_FLIGHT_PERIODS,
  type ConciergeRequestSource,
} from '../model/checkout_source.js';
import {
  RequestEditor,
  type RequestBusAsks,
  type RequestEditorProps,
  type RequestFlightAsks,
  type RequestHotelAsks,
  type RequestReason,
  type RequestRentalAsks,
} from '../request/request_editor.js';

type RequestOf<P extends ConciergeRequestSource['productType']> = Extract<
  ConciergeRequestSource,
  { productType: P }
>;

/** The value of an option list, or `undefined` for anything else (the empty
 *  "indifferent" choice included). */
function optionOf<T extends string>(options: readonly T[], value: string): T | undefined {
  return options.find((option) => option === value);
}

function flightAsks(
  request: RequestOf<'flight'>,
  state: UseConciergeRequestResult,
  copy: ConciergeCopy,
  locale: string,
): RequestFlightAsks {
  return {
    kind: 'flight',
    copy: {
      title: copy.flight.asksTitle,
      direct: copy.flight.direct,
      luggage: copy.flight.luggage,
      periodAny: copy.flight.periodAny,
    },
    onlyDirect: state.onlyDirect,
    onlyWithLuggage: state.onlyWithLuggage,
    onRestrictionsChange: (next) => {
      state.setOnlyDirect(next.onlyDirect);
      state.setOnlyWithLuggage(next.onlyWithLuggage);
    },
    segments: request.segments.map((segment, index) => ({
      key: `${segment.origin.iata}-${segment.destination.iata}-${segment.date}`,
      label: copy.flight.periodFor(
        `${segment.origin.iata} → ${segment.destination.iata}`,
        fmtWeekdayDayMonth(segment.date, locale),
      ),
      value: state.periods[index] ?? '',
    })),
    periodOptions: CONCIERGE_FLIGHT_PERIODS.map((period) => ({
      value: period,
      label: copy.period[period],
    })),
    onPeriodChange: (index, value) => {
      // Vazio = indiferente; o valor vem das próprias opções.
      state.setPeriod(index, optionOf(CONCIERGE_FLIGHT_PERIODS, value));
    },
  };
}

/** Hotel preferido (texto livre — a equipe resolve o hotel; o legado gravava
 *  "Usuário não determinou" quando vazio) e o tipo de cama do hotels-api. */
function hotelAsks(state: UseConciergeRequestResult, copy: ConciergeCopy): RequestHotelAsks {
  return {
    kind: 'hotel',
    copy: {
      preferredHotel: copy.hotel.preferredHotel,
      preferredHotelPlaceholder: copy.hotel.preferredHotelPlaceholder,
      bedType: copy.hotel.bedType,
      bedAny: copy.hotel.bed.UNDEFINED,
    },
    preferredHotel: state.preferredHotel,
    onPreferredHotelChange: state.setPreferredHotel,
    bedType: state.bedType ?? '',
    bedOptions: CONCIERGE_BED_TYPES.filter((bed) => bed !== 'UNDEFINED').map((bed) => ({
      value: bed,
      label: copy.hotel.bed[bed],
    })),
    onBedTypeChange: (value) => {
      state.setBedType(optionOf(CONCIERGE_BED_TYPES, value));
    },
  };
}

/** Categoria (chips) e locadora do carro, dos catálogos do rental-car-api. */
function rentalAsks(state: UseConciergeRequestResult, copy: ConciergeCopy): RequestRentalAsks {
  const toOption = (option: { id: string; name: string }) => ({
    value: option.id,
    label: option.name,
  });
  return {
    kind: 'rental',
    copy: copy.rental,
    options:
      state.rentalOptionsPhase === 'ready'
        ? {
            phase: 'ready',
            categories: state.rentalCategories.map(toOption),
            companies: state.rentalCompanies.map(toOption),
          }
        : state.rentalOptionsPhase === 'failed'
          ? { phase: 'failed', onRetry: state.retryRentalOptions }
          : { phase: 'loading' },
    category: state.categoryId,
    onCategoryChange: state.setCategoryId,
    company: state.companyId,
    onCompanyChange: state.setCompanyId,
  };
}

/** O período de cada perna e onde sentar — todos obrigatórios, como no app
 *  (`BusConciergeForm`); a classe é a lista fixa do bus-api, não a da política. */
function busAsks(
  request: RequestOf<'bus'>,
  state: UseConciergeRequestResult,
  copy: ConciergeCopy,
  locale: string,
): RequestBusAsks {
  const legs = [
    { leg: 'outbound' as const, date: request.outwardDate },
    ...(request.returnDate ? [{ leg: 'return' as const, date: request.returnDate }] : []),
  ];
  const periodOptions = CONCIERGE_FLIGHT_PERIODS.map((period) => ({
    value: period,
    label: copy.period[period],
  }));
  return {
    kind: 'bus',
    copy: {
      title: copy.bus.asksTitle,
      choose: copy.bus.choose,
    },
    fields: [
      ...legs.map(({ leg, date }, index) => ({
        key: `period-${leg}`,
        label: copy.bus.periodFor(copy.bus.leg[leg], fmtWeekdayDayMonth(date, locale)),
        value: state.periods[index] ?? '',
        options: periodOptions,
        onChange: (value: string) => {
          state.setPeriod(index, optionOf(CONCIERGE_FLIGHT_PERIODS, value));
        },
      })),
      {
        key: 'seat-position',
        label: copy.bus.seatPosition,
        value: state.busSeat.position ?? '',
        options: CONCIERGE_BUS_SEAT_POSITIONS.map((position) => ({
          value: position,
          label: copy.bus.seatPositions[position],
        })),
        onChange: (value: string) => {
          state.setBusSeat({ position: optionOf(CONCIERGE_BUS_SEAT_POSITIONS, value) });
        },
      },
      {
        key: 'row-position',
        label: copy.bus.rowPosition,
        value: state.busSeat.rowPosition ?? '',
        options: CONCIERGE_BUS_ROW_POSITIONS.map((rowPosition) => ({
          value: rowPosition,
          label: copy.bus.rowPositions[rowPosition],
        })),
        onChange: (value: string) => {
          state.setBusSeat({ rowPosition: optionOf(CONCIERGE_BUS_ROW_POSITIONS, value) });
        },
      },
      {
        key: 'seat-class',
        label: copy.bus.seatClass,
        value: state.busSeat.seatClass ?? '',
        options: CONCIERGE_BUS_SEAT_CLASSES.map((seatClass) => ({
          value: seatClass,
          label: copy.bus.seatClasses[seatClass],
        })),
        onChange: (value: string) => {
          state.setBusSeat({ seatClass: optionOf(CONCIERGE_BUS_SEAT_CLASSES, value) });
        },
      },
    ],
  };
}

/** As perguntas de cada produto. Os detalhes são obrigatórios no hotel, no
 *  ônibus e no carro — é o briefing com que a equipe cota (no carro o
 *  fornecedor recusa o pedido sem ele); a copy do cartão diz onde (`detailsHint`). */
function productAsks(
  request: ConciergeRequestSource,
  state: UseConciergeRequestResult,
  copy: ConciergeCopy,
  locale: string,
): RequestEditorProps['asks'] {
  switch (request.productType) {
    case 'flight':
      return flightAsks(request, state, copy, locale);
    case 'hotel':
      return hotelAsks(state, copy);
    case 'bus':
      return busAsks(request, state, copy, locale);
    case 'rental':
      return rentalAsks(state, copy);
    default: {
      const exhaustive: never = request;
      return exhaustive;
    }
  }
}

/** "Por que pedir à equipe" — o catálogo do concierge-api. Some quando o catálogo
 *  não respondeu (o pedido segue sem motivo). */
function requestReason(state: UseConciergeRequestResult, copy: ConciergeCopy): RequestReason {
  const title = copy.reasonTitle;
  switch (state.reasonsPhase) {
    case 'absent':
      return { phase: 'absent' };
    case 'loading':
      return { phase: 'loading', title, loading: copy.reasonLoading };
    case 'ready':
      return {
        phase: 'ready',
        title,
        options: state.reasons.map((reason) => ({
          value: conciergeReasonKey(reason),
          label: reason.name,
        })),
        value: state.reasonKey,
        onChange: state.setReasonKey,
      };
    default: {
      const exhaustive: never = state.reasonsPhase;
      return exhaustive;
    }
  }
}

export interface ConciergeRequestCardProps {
  request: ConciergeRequestSource;
  state: UseConciergeRequestResult;
}

/** O cartão do pedido à equipe VOLL: o `RequestEditor` ligado ao estado do
 *  pedido (`useConciergeRequest`). */
export function ConciergeRequestCard({
  request,
  state,
}: Readonly<ConciergeRequestCardProps>): ReactNode {
  const { copy: text, locale } = useCheckoutEnv();
  const copy = text.concierge;
  const card = copy.card[request.productType];
  return (
    <RequestEditor
      profile={request.profile}
      asks={productAsks(request, state, copy, locale)}
      reason={requestReason(state, copy)}
      details={{
        value: state.details,
        maxLength: CONCIERGE_DETAILS_MAX,
        // A dica existe exatamente onde os detalhes são obrigatórios.
        required: card.detailsHint !== undefined,
        onChange: state.setDetails,
      }}
      copy={{
        title: card.title,
        noPriceBadge: copy.noPriceBadge,
        profileLabel: copy.profileLabel,
        profileHint: copy.profileHint,
        optional: copy.optional,
        detailsLabel: copy.detailsLabel,
        detailsPlaceholder: card.detailsPlaceholder,
        ...(card.detailsHint ? { detailsHint: card.detailsHint } : {}),
      }}
    />
  );
}
